import { Icon } from '@natera/material/lib/icon'
import { useCallback, useEffect, useRef, useState, type PropsWithChildren } from 'react'
import { cn } from '~/shared/lib/cn'
import { IconButton } from '~/shared/ui/icon-button'
import { ScrollArea } from '~/shared/ui/scroll-area'

export function SearchView({
  className,
  children,
  value,
  onChange,
  placeholder,
  forceOpen = false,
  scrollable = true,
  inlineInput = false,
}: PropsWithChildren<{
  className?: string
  children: React.ReactNode
  value: string
  onChange: (value: string) => void
  placeholder: string
  forceOpen?: boolean
  scrollable?: boolean
  inlineInput?: boolean
}>) {
  const [isOpen, setIsOpen] = useState(forceOpen)
  const searchViewRef = useRef<HTMLDivElement>(null)

  const handleChange = useCallback(
    (value: string) => {
      onChange(value)
      if (!forceOpen) {
        setIsOpen(!!value)
      }
    },
    [onChange, forceOpen],
  )

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchViewRef.current && !searchViewRef.current.contains(event.target as Node)) {
        if (!forceOpen) {
          setIsOpen(false)
        }
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, forceOpen])

  return (
    <div
      className={cn(
        'relative w-full',
        {
          'h-12': inlineInput,
        },
        className,
      )}
      ref={searchViewRef}
    >
      <div
        className={cn('w-full overflow-hidden rounded-sm', {
          'absolute top-0 left-0 z-10 focus-within:shadow-[0px_0.25px_3px_0px_rgba(0,0,0,0.04),0px_2.75px_9px_0px_rgba(0,0,0,0.19)]':
            inlineInput,
          'shadow-[0px_0.25px_3px_0px_rgba(0,0,0,0.04),0px_2.75px_9px_0px_rgba(0,0,0,0.19)]':
            inlineInput && isOpen,
        })}
      >
        <div className="hover:bg-action-hovered border-search-enabled-border hover:border-search-hovered-border relative mx-4 my-2 h-12 overflow-hidden rounded-sm border">
          <Icon
            children="search"
            className="text-content-high-emphasis absolute top-1/2 left-0 mr-3 ml-4 -translate-y-1/2"
          />
          <input
            className={cn(
              'bg-surface-lowest box-border h-full w-full border-none pr-4 pl-13 text-[15px] leading-5 tracking-[0.25px] outline-none',
              {
                'pr-12': value,
              },
            )}
            name="search"
            placeholder={placeholder}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            autoComplete="off"
            onFocus={() => children && value && setIsOpen(true)}
          />
          {value && (
            <IconButton
              className="absolute top-1/2 right-0 mr-3 ml-1 -translate-y-1/2"
              size="medium"
              container="small"
              materialIcon="close"
              onClick={() => handleChange('')}
            />
          )}
        </div>

        {isOpen &&
          (scrollable ? (
            <ScrollArea className="bg-surface-lowest w-full">
              <div className="max-h-[300px]">{children}</div>
            </ScrollArea>
          ) : (
            <div className="bg-surface-lowest relative w-full">{children}</div>
          ))}
      </div>
    </div>
  )
}
